import { CpuType } from '@cast/shared-types';
import type { PartialNullableProps } from '@cast/shared-types';
import { ClusterAnomaly } from '@cast/types';

import { computeResourceKeyMap } from 'constants/maps';
import { ChartType } from 'types/charts';
import { ComputeSpendType } from 'types/cost-report';
import { NodeType } from 'types/nodes';
import { TopMostExpensiveGroupDatapoint } from 'types/top-items';

type Resource =
  (typeof computeResourceKeyMap)[keyof typeof computeResourceKeyMap];

type NodeTypeWithUptime = `${keyof Record<NodeType, string>}Uptime`;
type NodeTypeWithCost = `${keyof Record<NodeType, string>}Cost`;
type NodeTypeWithResourceCost = `${keyof Record<
  NodeType,
  string
>}${keyof Record<Resource, string>}Cost`;
type NodeTypeWithCostPerCpu = `${keyof Record<NodeType, string>}CostPerCpu`;
type NodeTypeWithCostPerGpu = `${keyof Record<NodeType, string>}CostPerGpu`;
type NodeTypeWithCostPerTpu = `${keyof Record<NodeType, string>}CostPerTpu`;
type NodeTypeWithCostPerGib = `${keyof Record<NodeType, string>}CostPerGib`;
type NodeTypeWithCpuCount = `${keyof Record<NodeType, string>}CpuCount`;
type NodeTypeWithGpuCount = `${keyof Record<NodeType, string>}GpuCount`;
type NodeTypeWithTpuCount = `${keyof Record<NodeType, string>}TpuCount`;
type NodeTypeWithRamGib = `${keyof Record<NodeType, string>}RamGib`;

type CostReportDataBase<T = number> = {
  timestamp: string;

  totalCpu: T;
  totalGpu: T;
  totalTpu: T;
  totalRamGib: T;
  totalStorage: T;

  totalCost: T;
  totalCpuCost: T;
  totalGpuCost: T;
  totalTpuCost: T;
  totalRamCost: T;
  totalStorageCost: T;

  normalizedCostPerCpu: T;
  normalizedCostPerGib: T;
  normalizedCostPerGpu: T;
  normalizedCostPerTpu: T;
  normalizedCostPerStorage: T;
  costPerStorage: T;

  forecast?: boolean; // true when calculated from moving average
  generated?: boolean; // true when datapoint is added to by us

  /** Populated for namespace reports when Cast AI fee estimation is enabled. */
  estimatedCastAiFee?: number | null;
  estimatedCombinedCost?: number | null;
} & Record<NodeTypeWithCost, T> &
  Record<NodeTypeWithResourceCost, T> &
  Record<NodeTypeWithCostPerCpu, T> &
  Record<NodeTypeWithCostPerGib, T> &
  Record<NodeTypeWithCostPerGpu, T> &
  Record<NodeTypeWithCostPerTpu, T> &
  Record<NodeTypeWithCpuCount, T> &
  Record<NodeTypeWithGpuCount, T> &
  Record<NodeTypeWithTpuCount, T> &
  Record<NodeTypeWithRamGib, T> &
  Record<NodeTypeWithUptime, T>;

export type HourlyClusterCostReportData<T = number> = CostReportDataBase<T>;

type NodeTypeWithPodCount = `${keyof Record<NodeType, string>}PodCount`;
type NodeTypeWithCostPerPod = `${keyof Record<NodeType, string>}CostPerPod`;
type ComputeSpendTypeWithForecast = `${keyof Record<
  ComputeSpendType,
  string
>}Forecast`;
type ComputeSpendTypeWithForecastDelta = `${keyof Record<
  ComputeSpendType,
  string
>}ForecastDelta`;

export type UptimeWorkloadCostReportData<T = number> = CostReportDataBase<T> & {
  totalPods: T;
  normalizedCostPerPod: T;
} & Record<NodeTypeWithPodCount, T> &
  Record<NodeTypeWithCostPerPod, T>;

export type CostReportDataByPeriod<T = number> =
  | HourlyClusterCostReportData<T>
  | UptimeWorkloadCostReportData<T>;

type HourlyClusterCostReportDataNullableProps = Exclude<
  keyof HourlyClusterCostReportData,
  'timestamp' | 'forecast'
>;

type UptimeWorkloadCostReportDataNullableProps = Exclude<
  keyof HourlyClusterCostReportData,
  'timestamp' | 'forecast'
>;

export type CostReportChartDataByPeriod<T = number> =
  | PartialNullableProps<
      HourlyClusterCostReportData<T>,
      HourlyClusterCostReportDataNullableProps
    >
  | PartialNullableProps<
      UptimeWorkloadCostReportData<T>,
      UptimeWorkloadCostReportDataNullableProps
    >;

export type AnomaliesAware = {
  anomalies: ClusterAnomaly[];
};

export type ComputeSpendChartData = {
  timestamp: string;
  forecast?: boolean;
} & AnomaliesAware &
  Partial<Record<ComputeSpendTypeWithForecast, number | null>> &
  Partial<Record<ComputeSpendTypeWithForecastDelta, number | null>> &
  Record<ComputeSpendType, number | null> &
  Record<NodeTypeWithCpuCount, number | null> &
  Record<NodeTypeWithRamGib, number | null>;

type NormalizedCostModeWithForecast = `${NormalizedCostMode}Forecast`;
type NormalizedCostModeWithForecastDelta = `${NormalizedCostMode}ForecastDelta`;

export type NormalizedCostChartData = Record<
  NormalizedCostMode,
  number | null
> &
  Partial<Record<NormalizedCostModeWithForecast, number | null>> &
  Partial<Record<NormalizedCostModeWithForecastDelta, number | null>> & {
    forecast?: boolean;
    timestamp: string;
  };

export type PodCountCostChartData = Record<PodCountMode, number | null> & {
  forecast?: boolean;
  timestamp: string;
};

export type TopMostExpensiveGroupsChartData =
  TopMostExpensiveGroupDatapoint[number];

export const CostReportChartType = {
  AREA: ChartType.AREA,
  BAR: ChartType.BAR,
  LINE: ChartType.LINE,
} as const;
export type CostReportChartType =
  (typeof CostReportChartType)[keyof typeof CostReportChartType];

export enum ComputeSpendMode {
  DAILY_COST = 'DAILY_COST',
  CUMULATIVE = 'CUMULATIVE',
}

export enum NormalizedCostMode {
  ON_DEMAND = CpuType.ON_DEMAND,
  FALLBACK = CpuType.FALLBACK,
  SPOT = CpuType.SPOT,
  NORMALIZED = CpuType.NORMALIZED,
}

export type DailyMetrics = {
  cost?: number;
  prevCost?: number;
};

export type HourlyCostPerResource = {
  normalizedSoFar: number;
  normalizedAverage: number;

  onDemandSoFar: number;
  onDemandAverage: number;

  fallbackSoFar: number;
  fallbackAverage: number;

  spotSoFar: number;
  spotAverage: number;
};

export type AggregatedMetrics = {
  computeCost: {
    hourly: number;
    daily: number;
    total: number;
    todayProjected: number;
    rangeSoFar: number;
    onDemandProjected: number;
    fallbackProjected: number;
    storageProjected: number;
    spotProjected: number;
    onDemandRangeSoFar: number;
    fallbackRangeSoFar: number;
    spotRangeSoFar: number;
    storageRangeSoFar: number;
  };
  hourlyCostPerCpu?: HourlyCostPerResource;
  hourlyCostPerRamGib?: HourlyCostPerResource;
  hourlyCostPerGpu?: HourlyCostPerResource;
  hourlyCostPerTpu?: HourlyCostPerResource;
  hourlyCostPerStorage?: HourlyCostPerResource;
  cpuCount?: {
    total: number;
    onDemand: number;
    fallback: number;
    spot: number;
  };
  podsCount?: {
    total: number;
    onDemand: number;
    fallback: number;
    spot: number;
  };
  podsCost?: {
    normalized: number;
    onDemand: number;
    fallback: number;
    spot: number;
  };
  monthlyForecast?: DailyMetrics;
  avgDailyCost?: DailyMetrics;
  avgMonthlyCost?: DailyMetrics;
  avgDailyCostPerCpu?: number;
  avgDailyCostPerGpu?: number;
  avgDailyCostPerTpu?: number;
  avgDailyCostPerGib?: number;
  avgDailyCostPerStorage?: number;
  workloadsCount?: number;
};

export type ResourceOfferingMultiplier = {
  [ComputeSpendType.ON_DEMAND]: number;
  [ComputeSpendType.FALLBACK]: number;
  [ComputeSpendType.SPOT]: number;
  [ComputeSpendType.STORAGE]: number;
};

export enum CostReportPodTab {
  COUNT = 'COUNT',
  COST = 'COST',
}

export enum PodCostMode {
  ON_DEMAND = CpuType.ON_DEMAND,
  FALLBACK = CpuType.FALLBACK,
  SPOT = CpuType.SPOT,
  NORMALIZED = CpuType.NORMALIZED,
}

export enum PodCountMode {
  ON_DEMAND = 'onDemand',
  FALLBACK = 'fallback',
  SPOT = 'spot',
  TOTAL = 'total',
}

export const isWorkloadReport = (
  data: CostReportChartDataByPeriod
): data is UptimeWorkloadCostReportData => {
  return (data as UptimeWorkloadCostReportData).onDemandUptime !== undefined;
};
